// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';
import {AttrsSchema, Component, ConfigOptions} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {emitTrackEvent, KEY_VALUES} from 'common/util/dom';
import {SvgIconEl} from 'common/components/svg-icon';

import {InputEl} from '../input';
import {TooltipEl} from '../tooltip';
import {BreadcrumbsState, LinkNode} from './types';
import {TrackingEvent} from './tracking';
import css from './index.inline.styl';

const COMPONENT_NAME = `mp-breadcrumbs`;

interface BreadcrumbsJsxProps {
  attrs?: BreadcrumbsAttrs;
  props: {
    linkNodes: LinkNode[];
  };
}

export enum BreadcrumbsTheme {
  Default = `default`,
  Path = `path`,
}

interface BreadcrumbsAttrs {
  'track-name'?: string;
  theme?: BreadcrumbsTheme;
}

const TitleInput = ({value, onInputChanged, onEditStop}: {value; onInputChanged; onEditStop?}) => {
  return (
    <InputEl
      attrs={{
        elref: `titleInput`,
      }}
      props={{
        value,
      }}
      on={{
        blur: (ev) => onInputChanged?.(ev.target.value),
        keydown: (ev: any) => {
          if (ev.key === KEY_VALUES.ESCAPE) {
            ev.preventDefault();
            onEditStop?.();
          } else if (ev.key === KEY_VALUES.ENTER) {
            ev.preventDefault();
            onInputChanged?.(ev.target.value);
          }
        },
      }}
      hook={{
        insert: (vnode: any) => {
          requestAnimationFrame(() => {
            vnode.elm.focus();
          });
        },
      }}
    />
  );
};

const BreadcrumbNode = ({
  node,
  idx,
  isEditing,
  totalNodes,
  onNodeClicked,
  onValueChanged,
}: {
  node: LinkNode;
  idx: number;
  isEditing: boolean;
  totalNodes: number;
  onNodeClicked: (node: LinkNode, idx: number) => void;
  onValueChanged?: (value: string) => void;
}) => {
  const isLastNode = idx === totalNodes - 1;

  if (isEditing) {
    return (
      <div
        sel='.breadcrumb-node'
        attrs={{elref: isLastNode ? `breadcrumbLastNode` : `breadcrumbNode`}}
        class={{'last-node': isLastNode, 'has-multiple-nodes': totalNodes > 1, 'editing-mode': true}}
      >
        <TitleInput value={node.label} onInputChanged={(value) => onValueChanged?.(value)} />
      </div>
    );
  }

  return (
    <div
      sel='.breadcrumb-node'
      class={{'last-node': isLastNode, 'has-multiple-nodes': totalNodes > 1, disabled: node.disabled}}
    >
      {!node.selected ? (
        <div
          sel='.step.link'
          on={{click: () => onNodeClicked?.(node, idx)}}
          attrs={isLastNode && {elref: `breadcrumbLastNode`}}
        >
          {node.url ? (
            <a attrs={{elref: isLastNode ? `breadcrumbLastNode` : `breadcrumbNode`, href: node.url}}>{node.label}</a>
          ) : (
            node.label
          )}
        </div>
      ) : (
        <div sel='.step.selected'>{node.label}</div>
      )}
      <TooltipEl
        attrs={{
          text: node.label,
          'max-width': `250px`,
          'break-word': true,
        }}
      />
    </div>
  );
};

const SeparatorIcon = ({theme}: {theme: BreadcrumbsTheme}) =>
  theme === BreadcrumbsTheme.Path ? <div sel='.slash'>/</div> : <SvgIconEl attrs={{icon: `caret-right`}} />;

export class Breadcrumbs extends Component<BreadcrumbsState, BreadcrumbsAttrs> {
  static get attrsSchema(): AttrsSchema<BreadcrumbsAttrs> {
    return {
      'track-name': {
        type: `string`,
        description: `If specified, fire a track event and track call`,
      },
      theme: {
        type: `string`,
        tsType: `BreadcrumbsTheme`,
        default: BreadcrumbsTheme.Default,
      },
      ...super.attrsSchema,
    };
  }
  get config(): ConfigOptions<BreadcrumbsState> {
    return {
      css,
      template: () => {
        return (
          <div sel='.breadcrumbs' class={{[`${this.attr(`theme`)}-theme`]: true}}>
            {this.state.linkNodes.map((linkedNode, idx) => (
              <>
                <BreadcrumbNode
                  node={linkedNode}
                  idx={idx}
                  isEditing={linkedNode.isInEditMode}
                  totalNodes={this.state.linkNodes.length}
                  onNodeClicked={(node, index) => {
                    this.handleNodeClicked(node, index);
                  }}
                  onValueChanged={(newLabel) => {
                    this.dispatchEvent(
                      new CustomEvent(`nodeUpdated`, {detail: {node: {...linkedNode, label: newLabel}}}),
                    );
                  }}
                />

                {idx < this.state.linkNodes.length - 1 ? <SeparatorIcon theme={this.attr(`theme`)} /> : null}
              </>
            ))}
          </div>
        );
      },
      useShadowDom: true,
      defaultState: {
        linkNodes: [],
      },
    };
  }

  handleNodeClicked(node, index) {
    this.dispatchEvent(new CustomEvent(`nodeClick`, {detail: {node, index}}));
    const props = {...node, index, eventName: this.attr(`track-name`)};
    if (this.attr(`track-name`)) {
      emitTrackEvent(this, {
        eventName: TrackingEvent.ClickedLink,
        eventPrefix: COMPONENT_NAME,
        props,
      });
    }
  }

  get linkNodes() {
    return this.state.linkNodes;
  }

  set linkNodes(linkNodes: LinkNode[]) {
    this.update({linkNodes});
  }
}

export const BreadcrumbsEl = defineAndMakeJsxFactory<BreadcrumbsJsxProps>(COMPONENT_NAME, Breadcrumbs);
export const BreadcrumbsReact = panel2react<BreadcrumbsJsxProps>(COMPONENT_NAME, Breadcrumbs);
